import 'leaflet/dist/leaflet.css';
import type {MapaDeSeguimiento, MarcadorDelMapa, PuntoDelMapa, ViajeDelMapa} from '@cnc/contracts';
import {latLngBounds} from 'leaflet';
import {useEffect, useMemo} from 'react';
import {CircleMarker, MapContainer, Polyline, Popup, TileLayer, Tooltip, useMap} from 'react-leaflet';
import {Link} from 'react-router';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {distanciaKm, puntoEnRuta, rutaMaritima, type Coordenada} from '../utilidades/rutas-maritimas';

// Mapa real (OpenStreetMap) con las exportaciones en curso. Con reserva
// vinculada, la ruta pasa por los puertos de cada tramo del booking y la
// carga se ubica en el tramo de hoy (puerto de salida, a bordo, transbordo o
// destino). Sin reserva, ruta y posicion se estiman con ETD y ETA. El punto
// pequeno es el ultimo evento real que informo la naviera.
export function MapaSeguimiento({mapa, cargando}: {mapa: MapaDeSeguimiento | undefined; cargando: boolean}) {
  const dibujables = useMemo(() => (mapa?.marcadores ?? []).map(preparar).filter(d => d !== null), [mapa]);
  const sinPuertos = (mapa?.marcadores.length ?? 0) - dibujables.length;
  const puntos = dibujables.flatMap(d => d.ruta);
  const puertos = useMemo(() => {
    const lista = new Map<string, Coordenada>();
    for (const m of mapa?.marcadores ?? []) {
      const deLaReserva = (m.viaje?.tramos ?? []).flatMap(t => [t.puntoDesde, t.puntoHacia]);
      for (const p of [m.puntoDeCarga, m.puntoDeDestino, ...m.escalas, ...deLaReserva])
        if (p) lista.set(p.nombre, [p.latitud, p.longitud]);
    }
    return [...lista.entries()];
  }, [mapa]);

  return (
    <section className={cargando ? 'opacity-60' : ''}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-apagado">
            {tr('Mapa de las exportaciones en curso', 'Map of exports in progress')}
          </h2>
          <div className="text-xs text-apagado">
            {tr(
              'Con reserva vinculada, la ruta pasa por los puertos y transbordos del booking y la carga se ubica según sus fechas (línea llena: lo ya recorrido). Sin reserva, ruta y posición se estiman con ETD y ETA. El punto pequeño es el último evento que informó la naviera. No sustituye el rastreo oficial.',
              'With a linked booking, the route goes through the ports and transshipments of the booking and the cargo is placed by its dates (solid line: already traveled). Without a booking, route and position are estimated from ETD and ETA. The small dot is the last event reported by the shipping line. It does not replace the official tracking.',
            )}
          </div>
        </div>
        {mapa && (
          <div className="text-xs text-apagado">
            {tr(`${numero(dibujables.length)} en el mapa`, `${numero(dibujables.length)} on the map`)}
            {sinPuertos > 0
              ? tr(
                  ` · ${numero(sinPuertos)} sin puertos con coordenadas`,
                  ` · ${numero(sinPuertos)} without ports with coordinates`,
                )
              : ''}
          </div>
        )}
      </div>
      <div className="mt-2 overflow-hidden rounded-lg border border-linea">
        <MapContainer center={[15, 20]} zoom={2} minZoom={2} scrollWheelZoom style={{height: 480, width: '100%'}}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {puertos.map(([nombre, c]) => (
            <CircleMarker
              key={nombre}
              center={c}
              radius={3}
              pathOptions={{color: '#173b35', fillColor: '#173b35', fillOpacity: 0.9}}
            >
              <Tooltip direction="top" offset={[0, -4]}>
                {nombre}
              </Tooltip>
            </CircleMarker>
          ))}
          {dibujables.flatMap(d =>
            d.segmentos.map((s, i) => (
              <Polyline
                key={`ruta-${d.marcador.codigo}-${i}`}
                positions={s.puntos}
                pathOptions={
                  s.recorrido
                    ? {color: d.color, weight: 3, opacity: 0.75}
                    : {color: d.color, weight: 2, opacity: 0.45, dashArray: '4 6'}
                }
              />
            )),
          )}
          {dibujables.map(d =>
            d.marcador.ultimoEvento?.punto ? (
              <CircleMarker
                key={`evento-${d.marcador.codigo}`}
                center={[d.marcador.ultimoEvento.punto.latitud, d.marcador.ultimoEvento.punto.longitud]}
                radius={4}
                pathOptions={{color: '#ffffff', weight: 1, fillColor: d.color, fillOpacity: 1}}
              >
                <Tooltip direction="bottom" offset={[0, 4]}>
                  {d.marcador.codigo}:{' '}
                  {tr(
                    `${d.marcador.ultimoEvento.tipo} en ${d.marcador.ultimoEvento.lugar ?? 'sin nombre'} (${fecha(d.marcador.ultimoEvento.fecha.slice(0, 10))})`,
                    `${d.marcador.ultimoEvento.tipo} at ${d.marcador.ultimoEvento.lugar ?? 'unnamed place'} (${fecha(d.marcador.ultimoEvento.fecha.slice(0, 10))})`,
                  )}
                </Tooltip>
              </CircleMarker>
            ) : null,
          )}
          {dibujables.map(d => (
            <CircleMarker
              key={`pos-${d.marcador.codigo}`}
              center={d.posicion}
              radius={8}
              className={`posicion-${d.marcador.codigo}`}
              pathOptions={{color: '#ffffff', weight: 2, fillColor: d.color, fillOpacity: 0.95}}
            >
              <Tooltip direction="top" offset={[0, -8]}>
                <b>{d.marcador.codigo}</b> ·{' '}
                {d.marcador.viaje ? situacionDe(d.marcador.viaje) : d.marcador.estadoEtiqueta}
              </Tooltip>
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold">
                    <Link to={`/seguimiento/${encodeURIComponent(d.marcador.codigo)}`}>{d.marcador.codigo}</Link>
                  </div>
                  <div>
                    {d.marcador.exportador ?? tr('De terceros', 'Third party')} · {d.marcador.estadoEtiqueta}
                  </div>
                  <div>
                    {d.marcador.puertoCarga ?? tr('Origen', 'Origin')} →{' '}
                    {d.marcador.puertoDestino ?? tr('Destino', 'Destination')}
                  </div>
                  <div>
                    ETD {fecha(d.marcador.etd)} · ETA {fecha(d.marcador.eta)}
                  </div>
                  {d.marcador.buque && (
                    <div>
                      {tr('Buque:', 'Vessel:')} {d.marcador.buque}
                    </div>
                  )}
                  {d.marcador.naviera && (
                    <div>
                      {tr('Naviera:', 'Shipping line:')} {d.marcador.naviera}
                    </div>
                  )}
                  {d.marcador.viaje && <ViajeEnElPopup viaje={d.marcador.viaje} />}
                  <div className="text-xs text-apagado">
                    {d.marcador.base === 'reserva'
                      ? tr(
                          `Ubicación según las fechas del booking ${d.marcador.viaje?.reserva ?? ''}`,
                          `Location from the dates of booking ${d.marcador.viaje?.reserva ?? ''}`,
                        )
                      : d.marcador.base === 'estimacion'
                        ? tr(
                            `Avance estimado ${numero((d.marcador.avance ?? 0) * 100)} %`,
                            `Estimated progress ${numero((d.marcador.avance ?? 0) * 100)} %`,
                          )
                        : tr(
                            'Sin fechas válidas: se dibuja en el puerto de carga',
                            'No valid dates: shown at the port of loading',
                          )}
                  </div>
                  {!d.marcador.viaje && d.marcador.escalas.length > 0 && (
                    <div className="text-xs text-apagado">
                      {tr('Escalas:', 'Port calls:')} {d.marcador.escalas.map(e => e.nombre).join(' → ')}
                    </div>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          ))}
          <AjustarVista puntos={puntos} />
        </MapContainer>
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-xs text-apagado">
        <Leyenda color={colorDe('Colombian Mint')} texto="Colombian Mint" />
        <Leyenda color={colorDe('Mona Minas')} texto="Mona Minas" />
        <Leyenda color={colorDe(null)} texto={tr('De terceros', 'Third party')} />
        {mapa && mapa.sinCoordenadas.length > 0 && (
          <span>
            {tr('Puertos sin coordenadas todavía:', 'Ports without coordinates yet:')} {mapa.sinCoordenadas.join(', ')}.
          </span>
        )}
      </div>
    </section>
  );
}

type Segmento = {puntos: Coordenada[]; recorrido: boolean};
type Dibujable = {
  marcador: MarcadorDelMapa;
  ruta: Coordenada[];
  segmentos: Segmento[];
  posicion: Coordenada;
  color: string;
};

const aCoordenada = (p: PuntoDelMapa): Coordenada => [p.latitud, p.longitud];

function preparar(m: MarcadorDelMapa): Dibujable | null {
  return porReserva(m) ?? porFechas(m);
}

// Con reserva: la ruta pasa por los puertos reales de cada tramo (transbordos
// incluidos) y la carga se ubica en el tramo que corresponde a hoy.
function porReserva(m: MarcadorDelMapa): Dibujable | null {
  const v = m.viaje;
  if (!v) return null;
  const partes = v.tramos.map((t, i) => {
    if (!t.puntoDesde || !t.puntoHacia) return null;
    const a = aCoordenada(t.puntoDesde);
    const b = aCoordenada(t.puntoHacia);
    const puntos = t.terrestre ? [a, b] : rutaMaritima(a, [], b);
    const recorrido =
      v.situacion === 'en-destino' || i < v.tramoActual || (i === v.tramoActual && v.situacion === 'navegando');
    return {puntos, recorrido, indice: i};
  });
  const actual = v.tramos[v.tramoActual];
  let posicion: Coordenada | null;
  if (v.situacion === 'navegando') {
    const parte = partes[v.tramoActual];
    posicion = parte ? puntoEnRuta(parte.puntos, v.avanceDelTramo) : null;
  } else if (v.situacion === 'en-destino') {
    posicion = actual?.puntoHacia ? aCoordenada(actual.puntoHacia) : null;
  } else {
    posicion = actual?.puntoDesde ? aCoordenada(actual.puntoDesde) : null;
  }
  const validas = partes.filter(p => p !== null);
  if (!posicion || !validas.length) return null;
  // En el tramo actual, lo recorrido va lleno y lo que falta punteado.
  const segmentos: Segmento[] = [];
  for (const p of validas) {
    if (p.indice === v.tramoActual && v.situacion === 'navegando') {
      const {hecho, falta} = partirRuta(p.puntos, v.avanceDelTramo);
      segmentos.push({puntos: hecho, recorrido: true}, {puntos: falta, recorrido: false});
    } else segmentos.push({puntos: p.puntos, recorrido: p.recorrido});
  }
  return {
    marcador: m,
    ruta: validas.flatMap(p => p.puntos),
    segmentos,
    posicion,
    color: colorDe(m.exportador),
  };
}

// Sin reserva: ruta estimada entre el puerto de carga y el destino, y
// posicion por el avance entre ETD y ETA.
function porFechas(m: MarcadorDelMapa): Dibujable | null {
  const origen = m.puntoDeCarga ?? m.escalas[0] ?? null;
  const destino = m.puntoDeDestino ?? m.escalas[m.escalas.length - 1] ?? null;
  if (!origen || !destino) return null;
  const escalas = m.escalas.map(aCoordenada);
  const ruta = rutaMaritima(aCoordenada(origen), escalas, aCoordenada(destino));
  return {
    marcador: m,
    ruta,
    segmentos: [{puntos: ruta, recorrido: false}],
    posicion: puntoEnRuta(ruta, m.avance ?? 0),
    color: colorDe(m.exportador),
  };
}

function partirRuta(ruta: Coordenada[], fraccion: number): {hecho: Coordenada[]; falta: Coordenada[]} {
  const punto = puntoEnRuta(ruta, fraccion);
  const largos = ruta.slice(1).map((p, i) => distanciaKm(ruta[i]!, p));
  const total = largos.reduce((a, b) => a + b, 0);
  let objetivo = Math.max(0, Math.min(1, fraccion)) * total;
  let corte = 0;
  while (corte < largos.length - 1 && objetivo > largos[corte]!) {
    objetivo -= largos[corte]!;
    corte++;
  }
  return {hecho: [...ruta.slice(0, corte + 1), punto], falta: [punto, ...ruta.slice(corte + 1)]};
}

// Una frase sobre donde va la carga segun la reserva.
function situacionDe(v: ViajeDelMapa): string {
  const cuando = (f: string | null | undefined) => (f ? fecha(f) : tr('fecha sin confirmar', 'date not confirmed'));
  const buque = v.buque ? tr(` en el buque ${v.buque}`, ` on vessel ${v.buque}`) : '';
  const tramo = v.tramos[v.tramoActual];
  switch (v.situacion) {
    case 'por-salir':
      return tr(
        `En ${v.lugar ?? 'el puerto de salida'}, por salir el ${cuando(v.proximo?.fecha)}${buque}.`,
        `At ${v.lugar ?? 'the port of departure'}, departing ${cuando(v.proximo?.fecha)}${buque}.`,
      );
    case 'en-transbordo':
      return tr(
        `En transbordo en ${v.lugar ?? 'un puerto intermedio'}; sigue el ${cuando(v.proximo?.fecha)}${buque}.`,
        `In transshipment at ${v.lugar ?? 'an intermediate port'}; continues ${cuando(v.proximo?.fecha)}${buque}.`,
      );
    case 'navegando': {
      const desde = tramo?.puntoDesde?.nombre ?? tramo?.desde ?? '';
      const hacia = tramo?.puntoHacia?.nombre ?? tramo?.hacia ?? '';
      return tramo?.terrestre
        ? tr(
            `En camino por tierra de ${desde} a ${hacia}; llega el ${cuando(v.proximo?.fecha)}.`,
            `On its way overland from ${desde} to ${hacia}; arrives ${cuando(v.proximo?.fecha)}.`,
          )
        : tr(
            `Navegando${buque} de ${desde} a ${hacia}; llega el ${cuando(v.proximo?.fecha)}.`,
            `Sailing${buque} from ${desde} to ${hacia}; arrives ${cuando(v.proximo?.fecha)}.`,
          );
    }
    case 'en-destino':
      return tr(
        `Llegó a ${v.lugar ?? 'su destino'} el ${cuando(tramo?.llegada)}.`,
        `Arrived at ${v.lugar ?? 'its destination'} on ${cuando(tramo?.llegada)}.`,
      );
  }
}

function ViajeEnElPopup({viaje: v}: {viaje: ViajeDelMapa}) {
  return (
    <div className="my-1">
      <div className="font-medium">{situacionDe(v)}</div>
      <ol className="mt-1 space-y-0.5 text-xs">
        {v.tramos.map((t, i) => {
          const hecho = v.situacion === 'en-destino' || i < v.tramoActual;
          const ahora = i === v.tramoActual && v.situacion !== 'en-destino';
          return (
            <li key={i} className={ahora ? 'font-semibold' : hecho ? 'text-apagado' : ''}>
              {hecho ? '✓ ' : ahora ? '▶ ' : '· '}
              {t.puntoDesde?.nombre ?? t.desde} → {t.puntoHacia?.nombre ?? t.hacia}
              {t.buque ? ` · ${t.buque}` : ''} · {fecha(t.salida)} → {fecha(t.llegada)}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function colorDe(exportador: string | null) {
  if (!exportador) return '#6f7c7f';
  return exportador === 'Colombian Mint' ? '#16775f' : exportador === 'Mona Minas' ? '#9a722c' : '#3b6fb6';
}

function AjustarVista({puntos}: {puntos: Coordenada[]}) {
  const map = useMap();
  useEffect(() => {
    if (!puntos.length) return;
    map.fitBounds(latLngBounds(puntos), {padding: [24, 24], maxZoom: 5});
  }, [map, puntos.length === 0 ? 0 : puntos[0]?.[0], puntos.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function Leyenda({color, texto}: {color: string; texto: string}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-full" style={{background: color}} />
      {texto}
    </span>
  );
}
